import type { VehicleLocationDTO } from "@/api/vtnz-backend/vehicles";
import { DateTime } from "luxon";

/**
 * How old a vehicle's last recorded location can get before it's flagged as
 * "stale" (likely lost contact) - on both the map's vehicle marker icon
 * (LeafletMap.vue, via mapStore.getMarkersWithRotation) and
 * VehicleDetails.vue's warning banner. Centralised here so both read the
 * same threshold rather than drifting apart.
 */
export const LOCATION_STALE_THRESHOLD_MINUTES = 15;

export type VehicleLocationFreshness = "fresh" | "stale";

/**
 * Classifies how old a vehicle's last recorded location is. Pure and cheap
 * by design - callers already have the location row in hand (e.g. from
 * vehicleStore.getLocationForVehicle) and just need a quick verdict, not a
 * fetch or a subscription.
 *
 * `now` is injectable for testing; defaults to the real current time.
 */
export function getVehicleLocationFreshness(
  location: VehicleLocationDTO,
  now: DateTime = DateTime.now(),
): VehicleLocationFreshness {
  const minutesSinceRecorded = now.diff(
    DateTime.fromISO(location.recorded_at),
    "minutes",
  ).minutes;

  if (minutesSinceRecorded >= LOCATION_STALE_THRESHOLD_MINUTES) return "stale";
  return "fresh";
}
